/* =====================================================================
   LUNEOR Cinematic — core (cart drawer, cursor, transitions, reveals)
   ===================================================================== */
(() => {
  const L = window.LUNEOR || {};
  const T = L.t || {};
  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => [...c.querySelectorAll(s)];
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const REDUCE = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FINE = matchMedia('(pointer:fine)').matches;
  const mouse = { x: innerWidth / 2, y: innerHeight / 2, rx: innerWidth / 2, ry: innerHeight / 2, moved: false };

  /* ---------- money ---------- */
  function money(cents) {
    if (L.currency) return new Intl.NumberFormat(L.locale || document.documentElement.lang || 'fr', { style: 'currency', currency: L.currency }).format(cents / 100);
    const fmt = L.moneyFormat || '${{amount}}';
    const n = (cents / 100);
    const two = n.toFixed(2), whole = Math.round(n).toString();
    const th = (s, sep, dec) => { const [i, d] = s.split('.'); return i.replace(/\B(?=(\d{3})+(?!\d))/g, sep) + (d ? dec + d : ''); };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) => ({
      amount: th(two, ',', '.'), amount_no_decimals: th(whole, ',', ''),
      amount_with_comma_separator: th(two, '.', ','), amount_no_decimals_with_comma_separator: th(whole, '.', ''),
      amount_with_apostrophe_separator: th(two, "'", '.'), amount_with_space_separator: th(two, ' ', ','),
    }[k] || two));
  }


  /* ---------- cut-out: remove a plain photo background so the product floats ----------
     Works on studio photos (white / light / plain background). Lifestyle photos
     with busy backgrounds are left untouched. Results are cached per URL. */
  const cutCache = {};
  function cutout(src, maxW = 1000) {
    if (!src) return Promise.resolve(null);
    if (cutCache[src]) return cutCache[src];
    return (cutCache[src] = new Promise(res => {
      const im = new Image(); im.crossOrigin = 'anonymous';
      im.onload = () => {
        try {
          const k = Math.min(1, maxW / im.naturalWidth), w = Math.round(im.naturalWidth * k), h = Math.round(im.naturalHeight * k);
          const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
          const c = cv.getContext('2d', { willReadFrequently: true }); c.drawImage(im, 0, 0, w, h);
          const id = c.getImageData(0, 0, w, h), d = id.data;
          // background colour = average of the border; bail out if the border is not uniform
          let br = 0, bg = 0, bb = 0, n = 0; const border = [];
          for (let x = 0; x < w; x += 3) border.push(x, (h - 1) * w + x);
          for (let y = 0; y < h; y += 3) border.push(y * w, y * w + w - 1);
          border.forEach(i => { br += d[i * 4]; bg += d[i * 4 + 1]; bb += d[i * 4 + 2]; n++; });
          br /= n; bg /= n; bb /= n;
          const dist = i => { const r = d[i * 4] - br, g = d[i * 4 + 1] - bg, b = d[i * 4 + 2] - bb; return Math.sqrt(r * r + g * g + b * b); };
          let same = 0; border.forEach(i => { if (dist(i) < 42) same++; });
          if (same / n < .72) return res(null);
          const T1 = 38, T2 = 78, seen = new Uint8Array(w * h), stack = [];
          border.forEach(i => { if (dist(i) < T1) { seen[i] = 1; stack.push(i); } });
          for (let x = 0; x < w; x++) for (const y of [0, h - 1]) { const i = y * w + x; if (!seen[i] && dist(i) < T1) { seen[i] = 1; stack.push(i); } }
          while (stack.length) {
            const i = stack.pop(); d[i * 4 + 3] = 0;
            const x = i % w, y = (i / w) | 0;
            const nb = [x > 0 ? i - 1 : -1, x < w - 1 ? i + 1 : -1, y > 0 ? i - w : -1, y < h - 1 ? i + w : -1];
            for (const j of nb) {
              if (j < 0 || seen[j]) continue;
              const dj = dist(j);
              if (dj < T1) { seen[j] = 1; stack.push(j); }
              else if (dj < T2) { seen[j] = 2; d[j * 4 + 3] = Math.round(255 * (dj - T1) / (T2 - T1)); } // soft edge
            }
          }
          c.putImageData(id, 0, 0);
          cv.toBlob(b => res(b ? URL.createObjectURL(b) : null), 'image/png');
        } catch (e) { res(null); }
      };
      im.onerror = () => res(null);
      im.src = src;
    }));
  }
  /* ---------- toast + copy ---------- */
  let tt;
  function toast(html) { const t = $('#toast'); if (!t) return; t.innerHTML = html; t.classList.add('on'); clearTimeout(tt); tt = setTimeout(() => t.classList.remove('on'), 2600); }
  function bindCopy(root = document) {
    $$('[data-copy]', root).forEach(b => { if (b._c) return; b._c = 1; b.addEventListener('click', () => {
      const c = b.dataset.copy, ok = () => toast('✦ ' + (T.copied || c));
      try { navigator.clipboard.writeText(c).then(ok, ok); } catch (e) { ok(); }
    }); });
  }

  /* ---------- cart (Shopify AJAX API) ---------- */
  const Cart = {
    data: null,
    async fetch() { const r = await fetch(L.routes.cart + '.js', { headers: { Accept: 'application/json' } }); this.data = await r.json(); this.render(); return this.data; },
    async add(form) {
      const r = await fetch(L.routes.cartAdd + '.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const j = await r.json();
      if (!r.ok) throw new Error(j.description || j.message || T.error || 'Error');
      await this.fetch(); this.bump(); return j;
    },
    async change(key, quantity) {
      const r = await fetch(L.routes.cartChange + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ id: key, quantity }) });
      this.data = await r.json(); this.render();
    },
    bump() { const b = $('#cartBtn'); if (!b) return; b.classList.remove('bump'); void b.offsetWidth; b.classList.add('bump'); },
    render() {
      const d = this.data; if (!d) return;
      const n = $('#cartN'); if (n) n.textContent = d.item_count;
      const box = $('#items'), df = $('#df'); if (!box) return;
      if (!d.item_count) {
        box.innerHTML = `<div class="empty"><b>${T.emptyTitle}</b>${T.emptyText}<br><br><a class="btn ghost" href="${L.routes.root}collections/all" data-tr>${T.explore}</a></div>`;
        df.style.display = 'none'; bindTransitions(box); return;
      }
      df.style.display = '';
      box.innerHTML = d.items.map(i => `<div class="item">
        <a class="ph" href="${i.url}">${i.image ? `<img src="${i.image}${i.image.includes('?') ? '&' : '?'}width=200" alt="">` : ''}</a>
        <div><h4><a href="${i.url}">${i.product_title}</a></h4><small>${i.product_has_only_default_variant ? '' : i.variant_title + ' · '}${money(i.final_price)}</small><br>
          <div class="qty"><button data-k="${i.key}" data-q="${i.quantity - 1}" aria-label="${T.decrease}">−</button><span>${i.quantity}</span><button data-k="${i.key}" data-q="${i.quantity + 1}" aria-label="${T.increase}">+</button></div>
          <button class="rm" data-k="${i.key}" data-q="0">${T.remove}</button></div>
        <div class="pr">${money(i.final_line_price)}</div></div>`).join('');
      $$('[data-k]', box).forEach(b => b.onclick = () => this.change(b.dataset.k, +b.dataset.q));
      const use = $('#useCode') ? $('#useCode').checked : false;
      const sub = d.total_price, disc = use ? Math.round(sub * (L.couponPct || 0) / 100) : 0;
      $('#sub').textContent = money(sub);
      $('#disc').textContent = '−' + money(disc);
      $('#discRow').style.display = use && disc ? '' : 'none';
      $('#tot').textContent = money(sub - disc);
      $('#checkout').href = use && L.coupon ? `/discount/${encodeURIComponent(L.coupon)}?redirect=/checkout` : '/checkout';
    },
    open() { $('#drawer')?.classList.add('on'); $('#scrim')?.classList.add('on'); $('#drawer')?.setAttribute('aria-hidden', 'false'); },
    close() { $('#drawer')?.classList.remove('on'); $('#scrim')?.classList.remove('on'); $('#drawer')?.setAttribute('aria-hidden', 'true'); },
  };

  function flyToCart(fromEl, src, done) {
    const cb = $('#cartBtn'); if (!fromEl || !cb || !src) { done && done(); return; }
    const r = fromEl.getBoundingClientRect(), c = cb.getBoundingClientRect();
    const f = document.createElement('div');
    f.style.cssText = `position:fixed;z-index:160;left:${r.left + r.width / 2 - 60}px;top:${r.top + r.height / 2 - 60}px;width:120px;height:120px;border-radius:50%;background:center/cover url("${src}");box-shadow:0 0 40px var(--c);transition:all .85s cubic-bezier(.6,-0.25,.3,1);pointer-events:none`;
    document.body.appendChild(f);
    requestAnimationFrame(() => requestAnimationFrame(() => Object.assign(f.style, { left: c.left + 11 + 'px', top: c.top + 11 + 'px', width: '20px', height: '20px', opacity: '.3' })));
    setTimeout(() => { f.remove(); done && done(); }, 900);
  }

  /* ---------- page transitions ---------- */
  function bindTransitions(root = document) {
    const w = $('#wipe'); if (!w) return;
    $$('a[data-tr]', root).forEach(a => {
      if (a._tr) return; a._tr = 1;
      a.addEventListener('click', e => {
        const href = a.getAttribute('href');
        if (!href || e.metaKey || e.ctrlKey || e.shiftKey || a.target === '_blank') return;
        const url = new URL(href, location.href);
        if (url.origin !== location.origin) return;
        $('#mnav')?.classList.remove('open');
        if (url.pathname === location.pathname && url.search === location.search && url.hash) {
          const t = document.getElementById(url.hash.slice(1));
          if (t) { e.preventDefault(); scrollTo({ top: t.getBoundingClientRect().top + scrollY - 60, behavior: 'smooth' }); }
          return;
        }
        e.preventDefault(); Cart.close();
        w.classList.remove('out'); w.classList.add('go');
        setTimeout(() => location.href = url.href, 720);
      });
    });
  }
  function pageIn() {
    const w = $('#wipe'); if (!w) return;
    setTimeout(() => w.classList.add('out'), 200);
    addEventListener('pageshow', e => { if (e.persisted) { w.classList.remove('go'); w.classList.add('out'); } });
  }

  /* ---------- cursor ---------- */
  function cursor() {
    addEventListener('mousemove', e => { mouse.x = e.clientX; mouse.y = e.clientY; }, { passive: true });
    const d = $('.cur'), r = $('.cur-r');
    if (!d || !FINE || REDUCE || L.designMode) { d && (d.style.display = r.style.display = 'none'); return; }
    document.documentElement.classList.add('fine');
    addEventListener('mousemove', () => { if (!mouse.moved) { mouse.moved = true; d.style.opacity = r.style.opacity = 1; } d.style.transform = `translate(${mouse.x}px,${mouse.y}px)`; }, { passive: true });
    document.addEventListener('mouseover', e => {
      const v = e.target.closest('[data-cursor]');
      r.classList.toggle('big', !!v); r.textContent = v ? v.dataset.cursor : '';
      r.classList.toggle('link', !v && !!e.target.closest('a,button'));
    });
    (function loop() { mouse.rx = lerp(mouse.rx, mouse.x, .16); mouse.ry = lerp(mouse.ry, mouse.y, .16); r.style.transform = `translate(${mouse.rx}px,${mouse.ry}px)`; requestAnimationFrame(loop); })();
  }

  /* ---------- header ---------- */
  function header() {
    const top = $('#top'); if (!top) return; let last = 0;
    const onScroll = () => {
      const y = scrollY;
      top.classList.toggle('solid', y > 30);
      last = y;
    };
    addEventListener('scroll', onScroll, { passive: true }); onScroll();
    const burger = $('#burger');
    burger && (burger.onclick = () => { const o = $('#mnav').classList.toggle('open'); burger.setAttribute('aria-expanded', o); });
    const cb = $('#cartBtn');
    if (cb && $('#drawer') && !document.body.classList.contains('template-cart')) cb.addEventListener('click', e => { e.preventDefault(); Cart.open(); });
    $('#closeCart') && ($('#closeCart').onclick = () => Cart.close());
    $('#scrim') && ($('#scrim').onclick = () => Cart.close());
    $('#useCode') && ($('#useCode').onchange = () => Cart.render());
    addEventListener('keydown', e => { if (e.key === 'Escape') Cart.close(); });
  }

  /* ---------- reveals / accordions / magnets / tilt / counters ---------- */
  function reveals(root = document) {
    if (!('IntersectionObserver' in window) || L.designMode) { $$('.rv,.mask,.gal .ph', root).forEach(e => e.classList.add('in')); return; }
    const io = new IntersectionObserver(es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); } }), { threshold: .12, rootMargin: '0px 0px -5% 0px' });
    $$('.rv, .mask, .gal .ph', root).forEach(el => io.observe(el));
  }
  function accordions(root = document) {
    $$('[data-acc]', root).forEach(acc => {
      $$(':scope > div > button', acc).forEach(b => { if (b._a) return; b._a = 1; b.addEventListener('click', () => {
        const item = b.parentElement, was = item.classList.contains('open');
        $$(':scope > div', acc).forEach(d => { d.classList.remove('open'); d.querySelector('button')?.setAttribute('aria-expanded', 'false'); });
        if (!was) { item.classList.add('open'); b.setAttribute('aria-expanded', 'true'); }
      }); });
    });
  }
  function magnets(root = document) {
    $$('.magnet', root).forEach(m => {
      m.addEventListener('mousemove', e => { const r = m.getBoundingClientRect(); m.style.transform = `translate(${(e.clientX - r.left - r.width / 2) * .35}px,${(e.clientY - r.top - r.height / 2) * .35}px)`; });
      m.addEventListener('mouseleave', () => m.style.transform = '');
    });
  }
  function tilts(root = document) {
    if (!FINE) return;
    $$('.pcard', root).forEach(el => {
      el.addEventListener('mousemove', e => {
        const r = el.getBoundingClientRect(), x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
        el.style.transform = `perspective(1000px) rotateY(${(x - .5) * 8}deg) rotateX(${(.5 - y) * 8}deg)`;
        el.style.setProperty('--gx', x * 100 + '%'); el.style.setProperty('--gy', y * 100 + '%');
      });
      el.addEventListener('mouseleave', () => el.style.transform = '');
    });
  }
  function counters(root = document) {
    const io = new IntersectionObserver(es => es.forEach(e => {
      if (!e.isIntersecting) return; io.unobserve(e.target);
      const el = e.target, raw = el.dataset.n, to = parseFloat(raw);
      if (isNaN(to) || /[^\d.]/.test(raw)) { el.textContent = raw; return; }
      const t0 = performance.now();
      (function tick(t) { const k = clamp((t - t0) / 1500, 0, 1); el.textContent = Math.round(to * (1 - Math.pow(1 - k, 3))); if (k < 1) requestAnimationFrame(tick); })(t0);
    }), { threshold: .5 });
    $$('[data-n]', root).forEach(el => io.observe(el));
  }
  function imgFallback(root = document) {
    $$('.ph img', root).forEach(im => {
      const bad = () => { im.classList.add('err'); im.parentElement.classList.add('err'); };
      if (im.complete && im.naturalWidth === 0 && im.src) bad(); else im.addEventListener('error', bad, { once: true });
    });
  }

  /* ---------- scroll-driven bits shared by all pages ---------- */
  const marquees = () => $$('[data-marquee]').map(el => ({ el, x: 0, speed: +(el.dataset.speed || 5) / 100 }));
  const hzs = () => $$('[data-hz]').map(el => ({ el, track: $('[data-hz-track]', el), prog: $('[data-hz-prog]', el), imgs: $$('.hz-card img', el) }));
  let MQ = [], HZ = [], PAR = [], lastY = scrollY, lastT = performance.now();
  function loop(now) {
    const dt = Math.min(50, now - lastT); lastT = now;
    const y = scrollY, vh = innerHeight, vel = Math.min(.4, Math.abs(y - lastY) * .02); lastY = y;
    MQ.forEach(m => { m.x -= dt * (m.speed + vel); const w = m.el.scrollWidth / 2; if (-m.x > w) m.x += w; m.el.style.transform = `translateX(${m.x}px)`; });
    HZ.forEach(h => {
      const r = h.el.getBoundingClientRect(), p = clamp(-r.top / (h.el.offsetHeight - vh), 0, 1);
      const max = Math.max(0, h.track.scrollWidth - innerWidth + 40);
      h.track.style.transform = `translateX(${-p * max}px)`; if (h.prog) h.prog.style.transform = `scaleX(${p})`;
      h.imgs.forEach(im => { const b = im.parentElement.getBoundingClientRect(); im.style.transform = `translateX(${((b.left + b.width / 2) / innerWidth - .5) * -12}%)`; });
    });
    PAR.forEach(im => { const r = im.parentElement.getBoundingClientRect(); if (r.bottom > 0 && r.top < vh) im.style.transform = `translateY(${((r.top + r.height / 2) / vh - .5) * -14}%)`; });
    requestAnimationFrame(loop);
  }

  function initAll(root = document) {
    bindCopy(root); bindTransitions(root); reveals(root); accordions(root); magnets(root); tilts(root); counters(root); imgFallback(root);
    MQ = marquees(); HZ = hzs(); PAR = $$('[data-par-wrap] img');
    $$('[data-world-link]', root).forEach(a => a.addEventListener('click', () => { const w = a.dataset.worldLink; document.documentElement.dataset.world = w; try { localStorage.setItem('luneor_world', w); } catch (e) {} }));
    const sort = $('[data-sort]'); sort && (sort.onchange = () => { const u = new URL(location.href); u.searchParams.set('sort_by', sort.value); location.href = u.href; });
  }

  window.Luneor = { T, cutout, $, $$, clamp, lerp, mouse, money, toast, Cart, flyToCart, REDUCE, FINE, initAll };

  try { const w = localStorage.getItem('luneor_world'); if (w) document.documentElement.dataset.world = w; } catch (e) {}
  document.addEventListener('DOMContentLoaded', () => {
    header(); cursor(); pageIn(); initAll();
    Cart.fetch().catch(() => {});
    requestAnimationFrame(loop);
    document.dispatchEvent(new CustomEvent('luneor:ready'));
  });
  // Theme editor: re-init a section after it's edited
  document.addEventListener('shopify:section:load', e => initAll(e.target));
})();
